// UniFed Lifecycle Series™ — target-date fund suggestions + allocation details.
import React, { useEffect, useState } from 'react';
import { Layers, Loader2 } from 'lucide-react';
import { lifecycleAPI } from '@/lib/api';

const FUND_KEY_LABEL = {
  a: 'A Fund',
  b: 'B Fund',
  c: 'C Fund',
  d: 'D Fund',
  e: 'E Fund',
};

const FUND_KEY_COLOR = {
  a: '#6B7280',
  b: '#2D5F8A',
  c: '#1B2A4A',
  d: '#8B6B3B',
  e: '#3B8C6E',
};

const AllocBar = ({ allocation }) => {
  // allocation is {a:72, b:6, ...}
  return (
    <div className="h-3 w-full rounded overflow-hidden flex border border-[#E2E5E9] bg-[#F0F1F3]">
      {['a', 'b', 'c', 'd', 'e'].map((k) => {
        const pct = allocation[k] || 0;
        if (!pct) return null;
        return (
          <div
            key={k}
            title={`${FUND_KEY_LABEL[k]}: ${pct}%`}
            style={{ width: `${pct}%`, backgroundColor: FUND_KEY_COLOR[k] }}
            className="h-full transition-all"
          />
        );
      })}
    </div>
  );
};

export const LifecycleFundsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    lifecycleAPI
      .list()
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="panel p-5" data-testid="lifecycle-funds-panel">
      <div className="flex items-center gap-2 mb-2">
        <Layers size={15} className="text-[#1B2A4A]" />
        <h2 className="text-sm font-semibold text-[#1B2A4A]">
          UniFed Lifecycle Series&trade;
        </h2>
        <span className="text-[10px] text-[#9CA3AF] font-mono">Target-date funds</span>
      </div>
      <p className="text-[12px] text-[#6B7280] mb-4 max-w-3xl">
        {data?.description ||
          'Professionally managed target-date funds whose allocation auto-shifts from equities to U.S. Treasury Securities as your target year approaches.'}
      </p>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-[#6B7280]">
          <Loader2 size={12} className="animate-spin" /> Loading lifecycle funds…
        </div>
      )}

      {data?.funds && (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
          data-testid="lifecycle-funds-grid"
        >
          {data.funds.map((f) => (
            <div
              key={f.code}
              data-testid={`lifecycle-fund-card-${f.code}`}
              className="border border-[#E2E5E9] rounded-lg p-3 bg-white hover:border-[#1B2A4A]/40 transition-colors"
            >
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-semibold text-[#1B2A4A]">{f.display_name}</p>
                <span className="text-[10px] font-mono text-[#9CA3AF]">
                  {f.expense_ratio_bps} bps
                </span>
              </div>
              <p className="text-[11px] text-[#6B7280] mb-2">{f.tagline}</p>
              <AllocBar allocation={f.allocation} />
              <div className="mt-2 flex flex-wrap gap-1">
                {['a', 'b', 'c', 'd', 'e'].map((k) => {
                  const pct = f.allocation[k] || 0;
                  if (!pct) return null;
                  return (
                    <span
                      key={k}
                      className="inline-flex items-center gap-1 text-[9px] font-semibold text-white px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: FUND_KEY_COLOR[k] }}
                    >
                      {FUND_KEY_LABEL[k]} {pct}%
                    </span>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-[10px] text-[#9CA3AF] italic mt-3">
        Lifecycle funds rebalance automatically — you do not need to adjust allocations as
        you age. Expense ratios shown in basis points (bps).
      </p>
    </div>
  );
};

export default LifecycleFundsPanel;
